import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { theme } from '@sweetgreen/garnish';

import type { BottomSheetExternalControls } from '@order/components';
import { BottomSheet } from '@order/components';

import { MOBILE_NAVBAR_HEIGHT } from '../../../../components/Header/constants';
import {
  BOTTOM_SHEET_TOP_HANDLER_HEIGHT,
  BOTTOM_SHEET_TOP_HANDLER_PLACEHOLDER_HEIGHT,
} from '../../../../constants';
import { useScrollToLocationCard } from '../../hooks';
import type { LocationBottomSheetProps } from '../../LocationScreen.types';
import { LocationResults } from '../LocationResults';
import { SingleLocationResult } from '../SingleLocationResult';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationBottomSheet = React.memo(
  (props: LocationBottomSheetProps) => {
    const {
      topOffset,
      locations,
      selectedLocation,
      locationCardsHeights,
      shouldShowSingleLocation,
      firstLocationCardHeight,
      isShowingLocationDetails,
      isShownLocationWarningModal,
      setShowLocationDetails,
      setBottomSheetExpanded,
      setIsLocationsBottomSheetCollapsed,
    } = props;

    const bottomSheetRef = useRef<BottomSheetExternalControls>();

    const selectedLocationCardHeight =
      locationCardsHeights.current.get(selectedLocation);
    const singleLocationCardTopOffset =
      locations.length > 1
        ? BOTTOM_SHEET_TOP_HANDLER_HEIGHT
        : BOTTOM_SHEET_TOP_HANDLER_PLACEHOLDER_HEIGHT;
    const singleLocationCardHeight =
      (selectedLocationCardHeight ?? firstLocationCardHeight) +
      singleLocationCardTopOffset;

    const snapPoints = useBottomSheetSnapPoints({
      topOffset,
      shouldShowSingleLocation,
      singleLocationCardHeight,
    });
    const selectedLocationDetails = locations.find(
      (location) => location.id === selectedLocation,
    );

    const shouldRenderLocationDetails = Boolean(
      shouldShowSingleLocation || isShowingLocationDetails,
    );

    // ─── HELPERS ─────────────────────────────────────────────────────

    const collapseSheet = useCallback(() => {
      bottomSheetRef.current?.collapse();
    }, []);

    const expandSheetToOneCardView = useCallback(() => {
      bottomSheetRef.current?.swipeTo(1);
    }, []);

    const expandFully = useCallback(() => {
      bottomSheetRef.current?.expand();
    }, []);

    const trackBottomSheetExpandedState = useCallback(
      (snapPointIndex: number, isFullExpanded: boolean) => {
        const isCollapsed = snapPointIndex === 0;

        if (!isFullExpanded) setShowLocationDetails(false);
        setBottomSheetExpanded(isFullExpanded);
        setIsLocationsBottomSheetCollapsed(isCollapsed);
      },
      [
        setBottomSheetExpanded,
        setIsLocationsBottomSheetCollapsed,
        setShowLocationDetails,
      ],
    );

    // ─── EFFECTS ─────────────────────────────────────────────────────

    // expand/collapse bottom sheet based on location/s results
    useEffect(() => {
      if (firstLocationCardHeight === 0) {
        collapseSheet();

        return;
      }

      expandSheetToOneCardView();
    }, [collapseSheet, expandSheetToOneCardView, firstLocationCardHeight]);

    // expand bottom sheet to "one card view" when user selects a location
    useEffect(() => {
      if (!selectedLocation || isShowingLocationDetails) return;
      expandSheetToOneCardView();
    }, [expandSheetToOneCardView, isShowingLocationDetails, selectedLocation]);

    // expand bottom sheet to "expanded view" when showing location details
    useEffect(() => {
      if (isShowingLocationDetails) expandFully();
      else expandSheetToOneCardView();
    }, [isShowingLocationDetails, expandFully, expandSheetToOneCardView]);

    // scrolls to active location card
    useScrollToLocationCard({
      locations,
      locationCardsHeights,
      scrollViewRef: bottomSheetRef.current?.scrollViewRef,
      selectedLocation,
      isShownLocationWarningModal,
      isShowingLocationDetails,
    });

    // ─────────────────────────────────────────────────────────────────

    const shouldRenderBottomSheet =
      typeof topOffset !== 'undefined' &&
      locations.length > 0 &&
      !isShownLocationWarningModal;

    return shouldRenderBottomSheet ? (
      <BottomSheet
        snapPoints={snapPoints}
        onSnapPointChange={trackBottomSheetExpandedState}
        isSwipingDisabled={locations.length === 1}
        ref={bottomSheetRef}
      >
        <View
          style={[
            styles.content,
            !isShowingLocationDetails && styles.contentPadding,
          ]}
        >
          {shouldRenderLocationDetails && selectedLocationDetails ? (
            <SingleLocationResult
              location={selectedLocationDetails}
              {...props}
            />
          ) : (
            <LocationResults {...props} />
          )}
        </View>
      </BottomSheet>
    ) : null;
  },
);

/**
 * Calculates 3 snap points for bottom sheet component.
 */
const useBottomSheetSnapPoints = (props: UseBottomSheetSnapPointsProps) => {
  const {
    topOffset = 0,
    shouldShowSingleLocation = false,
    singleLocationCardHeight,
  } = props;

  const { collapsedView, singleLocationCardView, expandedView } = useSnapPoints(
    topOffset,
    singleLocationCardHeight,
  );

  return useMemo<readonly number[]>(
    () =>
      shouldShowSingleLocation
        ? [collapsedView, singleLocationCardView]
        : [collapsedView, singleLocationCardView, expandedView],
    [
      shouldShowSingleLocation,
      collapsedView,
      singleLocationCardView,
      expandedView,
    ],
  );
};

const useSnapPoints = (topOffset: number, singleLocationCardHeight: number) => {
  const { height: screenHeight } = useWindowDimensions();
  const { top: safeAreaTop } = useSafeAreaInsets();

  return {
    collapsedView: theme.spacing.xlarge,
    singleLocationCardView: singleLocationCardHeight,
    expandedView: screenHeight - topOffset - safeAreaTop - MOBILE_NAVBAR_HEIGHT,
  };
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  contentPadding: {
    paddingHorizontal: theme.spacing.medium,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseBottomSheetSnapPointsProps = {
  readonly topOffset?: number;
  readonly shouldShowSingleLocation?: boolean;
  readonly singleLocationCardHeight: number;
};
